import type {
  BlockId,
  StepId
} from '@epilot/journey-logic-commons/src/types/next'
import { LOGIC_TRIGGER } from '@epilot/journey-logic-commons/src/types/next'
import { reduce } from 'radashi'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useThrottledCallback } from 'use-debounce'
import { shallow } from 'zustand/shallow'

import { Engine } from '../../logics/engine'
import type {
  LogicEvent,
  RuntimeContext,
  RuntimeFacts
} from '../../logics/types'
import { appStore } from '../../store'
import { pushLogicEvents } from '../../store/actions/pushLogicEvents'
import { setLogicsLock } from '../../store/actions/setLogicsLock'
import {
  selectBlockById,
  selectJourneyOrgId,
  selectLogics,
  selectStepById
} from '../../store/selectors/config'
import {
  selectAppParamsOrThrows,
  selectBlockValues,
  selectContextParameterValues,
  selectCurrentStep,
  selectLogicsLock
} from '../../store/selectors/runtime'
import { debug } from '../../utils/debug'
import { useAppStoreSelector } from '../useAppStoreSelector'
import { useSelfUpdatingRef } from '../useSelfUpdatingRef'

/**
 * Listens to relevant state changes and provides the interface with the Logics engine,
 * allowing to run evaluations in response to state changes or user interaction (i.e. user clicked next).
 */
export const useLogicsEngine = () => {
  const { preview } = useAppStoreSelector(selectAppParamsOrThrows)
  const engineRef = useRef(new Engine())
  const organizationId = useAppStoreSelector(selectJourneyOrgId)
  const locked = useAppStoreSelector(selectLogicsLock)
  const logics = useAppStoreSelector(selectLogics, shallow)
  const blockValues = useAppStoreSelector(selectBlockValues, shallow)
  const contextParameters = useAppStoreSelector(
    selectContextParameterValues,
    shallow
  )
  const currentStepId = useAppStoreSelector(selectCurrentStep)?.id

  const facts = useMemo<RuntimeFacts>(
    () => ({
      blocks: blockValues,
      contextParameters
    }),
    [blockValues, contextParameters]
  )

  const contextRef = useSelfUpdatingRef<RuntimeContext>({
    organizationId,
    currentStepId,
    blockGetter: (blockId: BlockId) => {
      return selectBlockById(appStore.getState(), blockId)
    },
    stepGetter: (stepId: StepId) => {
      return selectStepById(appStore.getState(), stepId)
    }
  })

  const _runner = useCallback(
    async (
      triggers: LOGIC_TRIGGER[],
      factValues: RuntimeFacts,
      context: RuntimeContext
    ) => {
      if (locked || preview) return // exit early

      const events = await reduce<LOGIC_TRIGGER, LogicEvent[]>(
        triggers,
        async (acc, trigger) => {
          const partial = await engineRef.current.run(
            trigger,
            factValues,
            context
          )

          acc.push(...partial)

          return acc
        },
        []
      )

      pushLogicEvents({ events })
    },
    [locked, preview]
  )

  const throttledRunner = useThrottledCallback(_runner, 70, {
    leading: false,
    trailing: true
  })

  useEffect(
    // ingest logics whenever they change
    () => {
      engineRef.current.ingestLogics(logics)
      debug('[useLogicsEngine] Logics were ingested', { logics })
    },
    [logics]
  )

  useEffect(
    // run the engine whenever the facts change
    () =>
      void throttledRunner(
        [LOGIC_TRIGGER.VALUE_CHANGED],
        facts,
        contextRef.current // not a dependency
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [throttledRunner, facts]
  )

  return useCallback(
    // run the engine for all logic triggers
    async () => {
      try {
        setLogicsLock({ locked: true })

        await _runner(Object.values(LOGIC_TRIGGER), facts, contextRef.current)
      } finally {
        setLogicsLock({ locked: false })
      }
    },
    [_runner, facts, contextRef]
  )
}
